/**
 * Anmeldung für Berichte mit Leserliste (seit 07.10.2026).
 *
 * Der Leser gibt seine Adresse ein, bekommt einen 8-stelligen Code per Mail
 * und tauscht ihn gegen eine Sitzung. Die Sitzung gilt 30 Tage und nur für
 * diesen einen Bericht; sie liegt im localStorage unter dem Token des Links.
 * Kein Supabase-Auth: ein Leser ist kein Konto bei 99takes. Server-Seite und
 * Begründung: supabase/functions/_shared/sim/bericht_zugang.ts.
 *
 * Ob eine Adresse auf der Liste steht, verrät die Seite nicht: nach der
 * Eingabe heißt es immer „wenn sie eingeladen ist, kommt ein Code".
 */

import { useState } from 'react';
import { SUPABASE_URL } from './config';
import { browserSprache, t } from './sprache';

const schluessel = (token: string) => `99takes-bericht-sitzung:${token}`;

/** localStorage kann fehlen oder werfen (privates Fenster, gesperrte Daten). */
export function leseSitzung(token: string): string {
  try { return localStorage.getItem(schluessel(token)) ?? ''; } catch { return ''; }
}

export function merkeSitzung(token: string, sitzung: string) {
  try { localStorage.setItem(schluessel(token), sitzung); } catch (e) { console.warn('[bericht-zugang] Sitzung nicht gespeichert', e); }
}

export function vergissSitzung(token: string) {
  try { localStorage.removeItem(schluessel(token)); } catch { /* nichts zu tun */ }
}

async function sende(body: Record<string, string>): Promise<{ ok: boolean; status: number; data: any }> {
  try {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    return { ok: res.ok, status: res.status, data: await res.json().catch(() => ({})) };
  } catch {
    return { ok: false, status: 0, data: { error: t('Keine Verbindung. Prüf deine Internetverbindung.', 'No connection. Check your internet connection.') } };
  }
}

const FELD = 'mt-1 block w-full rounded-md border border-[#cbd3d1] bg-white px-3 py-2.5 text-[1rem] text-[#1d1f26] focus:border-[#1f6964] focus:outline-none';
const KNOPF = 'mt-4 inline-flex min-h-11 cursor-pointer items-center rounded-md border-0 bg-[#1f6964] px-5 text-[0.9375rem] font-semibold text-white hover:bg-[#87481a] disabled:cursor-wait disabled:opacity-70';
const LEISE = 'cursor-pointer border-0 bg-transparent p-0 text-[0.875rem] text-[#5f646b] underline underline-offset-[3px] hover:text-[#1d1f26]';

export function LeserAnmeldung({ token, onAngemeldet }: { token: string; onAngemeldet: () => void }) {
  const [schritt, setSchritt] = useState<'adresse' | 'code'>('adresse');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [fehler, setFehler] = useState('');
  const [arbeitet, setArbeitet] = useState(false);

  async function codeAnfordern(e: React.FormEvent) {
    e.preventDefault();
    setFehler('');
    setArbeitet(true);
    // Der Leser hat kein Konto: die Code-Mail kommt in der Sprache seines Browsers.
    const r = await sende({ aktion: 'code', t: token, email: email.trim(), sprache: browserSprache() });
    setArbeitet(false);
    if (!r.ok) { setFehler(r.data?.error ?? t('Das hat nicht geklappt. Versuch es gleich noch einmal.', 'That did not work. Try again in a moment.')); return; }
    setCode('');
    setSchritt('code');
  }

  async function codePruefen(e: React.FormEvent) {
    e.preventDefault();
    setFehler('');
    setArbeitet(true);
    const r = await sende({ aktion: 'pruefen', t: token, email: email.trim(), code: code.replace(/\D/g, '') });
    setArbeitet(false);
    if (!r.ok || !r.data?.sitzung) { setFehler(r.data?.error ?? t('Der Code stimmt nicht oder ist abgelaufen.', 'The code is wrong or has expired.')); return; }
    merkeSitzung(token, r.data.sitzung);
    onAngemeldet();
  }

  if (schritt === 'adresse') {
    return (
      <form onSubmit={codeAnfordern} className="max-w-[60ch]">
        <p className="mt-0 mb-5 leading-relaxed text-[#5f646b]">
          {t('Gib die E-Mail-Adresse ein, an die die Einladung ging. Wir schicken dir einen Code, mit dem du den Bericht öffnest.',
            'Enter the email address the invitation went to. We will send you a code that opens the report.')}
        </p>
        <label htmlFor="leser-email" className="block text-[0.875rem] font-medium text-[#2f3238]">{t('E-Mail-Adresse', 'Email address')}</label>
        <input
          id="leser-email" type="email" required autoComplete="email" inputMode="email" autoFocus
          value={email} onChange={e => setEmail(e.target.value)} className={`${FELD} max-w-[24rem]`}
        />
        {fehler && <p role="alert" className="mt-3 mb-0 text-[0.875rem] text-[#a3341f]">{fehler}</p>}
        <button type="submit" disabled={arbeitet} className={KNOPF}>{arbeitet ? t('Wird geschickt …', 'Sending …') : t('Code schicken', 'Send code')}</button>
      </form>
    );
  }

  return (
    <form onSubmit={codePruefen} className="max-w-[60ch]">
      <p className="mt-0 mb-5 leading-relaxed text-[#5f646b]">
        {t(<>
        Wenn <strong className="font-semibold text-[#1d1f26] [overflow-wrap:anywhere]">{email.trim()}</strong> eingeladen ist, kommt gleich eine Mail
        mit einem 8-stelligen Code. Er gilt 15 Minuten. Schau auch im Spam-Ordner nach.
        </>, <>
        If <strong className="font-semibold text-[#1d1f26] [overflow-wrap:anywhere]">{email.trim()}</strong> is invited, an email with an 8-digit code
        will arrive shortly. It is valid for 15 minutes. Check your spam folder too.
        </>)}
      </p>
      <label htmlFor="leser-code" className="block text-[0.875rem] font-medium text-[#2f3238]">{t('Code', 'Code')}</label>
      <input
        id="leser-code" required autoComplete="one-time-code" inputMode="numeric" autoFocus maxLength={12}
        value={code} onChange={e => setCode(e.target.value)}
        className={`${FELD} max-w-[14rem] font-mono tracking-[0.2em]`}
      />
      {fehler && <p role="alert" className="mt-3 mb-0 text-[0.875rem] text-[#a3341f]">{fehler}</p>}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <button type="submit" disabled={arbeitet} className={KNOPF}>{arbeitet ? t('Wird geprüft …', 'Checking …') : t('Bericht öffnen', 'Open report')}</button>
        <button type="button" className={`${LEISE} mt-4`} onClick={() => { setFehler(''); setSchritt('adresse'); }}>
          {t('Andere Adresse oder neuer Code', 'Different address or new code')}
        </button>
      </div>
    </form>
  );
}
